/*
  ============================================================
  PIANZI ANNOUNCEMENT BAR
  Rotation, countdowns, autoplay and marquee
  ============================================================
*/

(() => {
  'use strict';

  const SECTION_SELECTOR =
    '[data-pianzi-announcement]';

  class PianziAnnouncementBar {
    constructor(section) {
      this.section = section;

      this.viewport = section.querySelector(
        '[data-pianzi-announcement-viewport]'
      );

      this.track = section.querySelector(
        '[data-pianzi-announcement-track]'
      );

      this.slides = Array.from(
        section.querySelectorAll(
          '[data-pianzi-announcement-slide]'
        )
      );

      this.previousButton = section.querySelector(
        '[data-pianzi-announcement-previous]'
      );

      this.nextButton = section.querySelector(
        '[data-pianzi-announcement-next]'
      );

      this.pauseButton = section.querySelector(
        '[data-pianzi-announcement-pause]'
      );

      this.countdowns = Array.from(
        section.querySelectorAll(
          '[data-pianzi-countdown]'
        )
      );

      this.displayMode =
        section.dataset.displayMode || 'static';

      this.autoplayEnabled =
        section.dataset.autoplay === 'true';

      this.autoplaySpeed =
        Number(section.dataset.autoplaySpeed) || 5000;

      this.pauseOnHover =
        section.dataset.pauseOnHover === 'true';

      this.wrapNavigation =
        section.dataset.wrapNavigation !== 'false';

      this.currentIndex = 0;
      this.isPaused = false;
      this.isHoverPaused = false;

      this.autoplayTimer = null;
      this.countdownTimer = null;

      this.marqueeCloned = false;

      this.boundHandleVisibilityChange =
        this.handleVisibilityChange.bind(this);

      this.boundHandleMouseEnter =
        this.handleMouseEnter.bind(this);

      this.boundHandleMouseLeave =
        this.handleMouseLeave.bind(this);

      this.init();
    }

    init() {
      this.setupSlides();
      this.setupControls();
      this.setupHoverPause();
      this.setupCountdowns();

      if (this.displayMode === 'marquee') {
        this.setupMarquee();
      }

      if (
        this.displayMode === 'rotating' &&
        this.autoplayEnabled &&
        this.slides.length > 1
      ) {
        this.startAutoplay();
      }

      document.addEventListener(
        'visibilitychange',
        this.boundHandleVisibilityChange
      );

      this.section.pianziAnnouncementBar = this;
    }

    /*
      ============================================================
      SLIDES
      ============================================================
    */

    setupSlides() {
      if (!this.slides.length) return;

      let activeIndex =
        this.slides.findIndex((slide) =>
          slide.classList.contains('is-active')
        );

      if (activeIndex < 0) {
        activeIndex = 0;
      }

      this.showSlide(activeIndex, false);
    }

    showSlide(index, restartAutoplay = true) {
      if (!this.slides.length) return;

      const safeIndex =
        Math.max(
          0,
          Math.min(index, this.slides.length - 1)
        );

      this.currentIndex = safeIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive =
          slideIndex === safeIndex;

        slide.classList.toggle(
          'is-active',
          isActive
        );

        slide.setAttribute(
          'aria-hidden',
          isActive ? 'false' : 'true'
        );
      });

      this.updateNavigationState();

      if (
        restartAutoplay &&
        this.displayMode === 'rotating' &&
        this.autoplayEnabled &&
        !this.isPaused &&
        !this.isHoverPaused
      ) {
        this.restartAutoplay();
      }
    }

    showNext() {
      if (this.slides.length < 2) return;

      let nextIndex =
        this.currentIndex + 1;

      if (nextIndex >= this.slides.length) {
        nextIndex = this.wrapNavigation
          ? 0
          : this.slides.length - 1;
      }

      this.showSlide(nextIndex);
    }

    showPrevious() {
      if (this.slides.length < 2) return;

      let previousIndex =
        this.currentIndex - 1;

      if (previousIndex < 0) {
        previousIndex = this.wrapNavigation
          ? this.slides.length - 1
          : 0;
      }

      this.showSlide(previousIndex);
    }

    updateNavigationState() {
      if (this.wrapNavigation) {
        if (this.previousButton) {
          this.previousButton.disabled = false;
        }

        if (this.nextButton) {
          this.nextButton.disabled = false;
        }

        return;
      }

      if (this.previousButton) {
        this.previousButton.disabled =
          this.currentIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled =
          this.currentIndex ===
          this.slides.length - 1;
      }
    }

    /*
      ============================================================
      CONTROLS
      ============================================================
    */

    setupControls() {
      this.previousButton?.addEventListener(
        'click',
        () => {
          this.showPrevious();
        }
      );

      this.nextButton?.addEventListener(
        'click',
        () => {
          this.showNext();
        }
      );

      this.pauseButton?.addEventListener(
        'click',
        () => {
          this.togglePause();
        }
      );
    }

    togglePause() {
      this.isPaused = !this.isPaused;

      this.pauseButton?.classList.toggle(
        'is-paused',
        this.isPaused
      );

      this.pauseButton?.setAttribute(
        'aria-pressed',
        this.isPaused ? 'true' : 'false'
      );

      this.pauseButton?.setAttribute(
        'aria-label',
        this.isPaused
          ? 'Play announcements'
          : 'Pause announcements'
      );

      if (this.isPaused) {
        this.stopAutoplay();
      } else if (
        this.autoplayEnabled &&
        !this.isHoverPaused
      ) {
        this.startAutoplay();
      }
    }

    /*
      ============================================================
      AUTOPLAY
      ============================================================
    */

    startAutoplay() {
      if (
        this.displayMode !== 'rotating' ||
        !this.autoplayEnabled ||
        this.slides.length < 2 ||
        this.isPaused ||
        this.isHoverPaused ||
        document.hidden
      ) {
        return;
      }

      this.stopAutoplay();

      this.autoplayTimer = window.setInterval(
        () => {
          this.showNext();
        },
        this.autoplaySpeed
      );
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(
          this.autoplayTimer
        );

        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    /*
      ============================================================
      HOVER PAUSE
      ============================================================
    */

    setupHoverPause() {
      if (!this.pauseOnHover) return;

      this.section.addEventListener(
        'mouseenter',
        this.boundHandleMouseEnter
      );

      this.section.addEventListener(
        'mouseleave',
        this.boundHandleMouseLeave
      );
    }

    handleMouseEnter() {
      this.isHoverPaused = true;
      this.stopAutoplay();

      if (this.displayMode === 'marquee') {
        this.track?.style.setProperty(
          'animation-play-state',
          'paused'
        );
      }
    }

    handleMouseLeave() {
      this.isHoverPaused = false;

      if (this.displayMode === 'marquee') {
        this.track?.style.removeProperty(
          'animation-play-state'
        );
      }

      if (
        this.autoplayEnabled &&
        !this.isPaused
      ) {
        this.startAutoplay();
      }
    }

    /*
      ============================================================
      COUNTDOWNS
      ============================================================
    */

    setupCountdowns() {
      if (!this.countdowns.length) return;

      this.updateCountdowns();

      this.countdownTimer =
        window.setInterval(() => {
          this.updateCountdowns();
        }, 1000);
    }

    updateCountdowns() {
      this.countdowns.forEach((countdown) => {
        this.updateCountdown(countdown);
      });
    }

    updateCountdown(countdown) {
      const dateString =
        countdown.dataset.countdownDate;

      if (!dateString) return;

      const targetDate =
        this.parseCountdownDate(
          dateString,
          countdown.dataset.countdownTimezone
        );

      if (!targetDate) {
        countdown.hidden = true;
        return;
      }

      const difference =
        targetDate.getTime() - Date.now();

      if (difference <= 0) {
        this.renderExpiredCountdown(
          countdown
        );

        return;
      }

      const totalSeconds =
        Math.floor(difference / 1000);

      const days =
        Math.floor(
          totalSeconds / 86400
        );

      const hours =
        Math.floor(
          (totalSeconds % 86400) / 3600
        );

      const minutes =
        Math.floor(
          (totalSeconds % 3600) / 60
        );

      const seconds =
        totalSeconds % 60;

      this.setCountdownValue(
        countdown,
        '[data-pianzi-countdown-days]',
        days
      );

      this.setCountdownValue(
        countdown,
        '[data-pianzi-countdown-hours]',
        hours
      );

      this.setCountdownValue(
        countdown,
        '[data-pianzi-countdown-minutes]',
        minutes
      );

      this.setCountdownValue(
        countdown,
        '[data-pianzi-countdown-seconds]',
        seconds
      );
    }

    parseCountdownDate(
      dateString,
      timezone
    ) {
      /*
        Recommended format:
        2026-08-09T20:00:00-04:00

        When an explicit UTC offset is already included,
        the browser will interpret it correctly.
      */

      const hasTimezone =
        /(?:Z|[+-]\d{2}:\d{2})$/i.test(
          dateString
        );

      let normalizedDate =
        dateString.trim();

      if (
        !hasTimezone &&
        timezone &&
        /^[+-]\d{2}:\d{2}$/.test(timezone)
      ) {
        normalizedDate += timezone;
      }

      const targetDate =
        new Date(normalizedDate);

      if (
        Number.isNaN(
          targetDate.getTime()
        )
      ) {
        return null;
      }

      return targetDate;
    }

    setCountdownValue(
      countdown,
      selector,
      value
    ) {
      const element =
        countdown.querySelector(selector);

      if (!element) return;

      element.textContent =
        String(value).padStart(2, '0');
    }

    renderExpiredCountdown(countdown) {
      const expiredText =
        countdown.dataset.expiredText ||
        'LIVE NOW';

      countdown.classList.add(
        'is-expired'
      );

      countdown.innerHTML = `
        <span class="pianzi-announcement__countdown-expired">
          ${this.escapeHtml(expiredText)}
        </span>
      `;
    }

    /*
      ============================================================
      MARQUEE
      ============================================================
    */

    setupMarquee() {
      if (
        !this.track ||
        !this.slides.length ||
        this.marqueeCloned
      ) {
        return;
      }

      /*
        Duplicate the existing slides once so the track can
        loop without revealing an empty gap.
      */

      const originalSlides =
        Array.from(
          this.track.querySelectorAll(
            '[data-pianzi-announcement-slide]'
          )
        );

      originalSlides.forEach((slide) => {
        const clone =
          slide.cloneNode(true);

        clone.removeAttribute(
          'data-block-id'
        );

        clone.removeAttribute(
          'data-index'
        );

        clone.removeAttribute(
          'aria-hidden'
        );

        clone.removeAttribute(
          'id'
        );

        clone.setAttribute(
          'aria-hidden',
          'true'
        );

        clone.classList.add(
          'is-active',
          'is-marquee-clone'
        );

        clone.querySelectorAll('[id]').forEach(
          (element) => {
            element.removeAttribute('id');
          }
        );

        this.track.appendChild(clone);
      });

      this.marqueeCloned = true;
    }

    /*
      ============================================================
      PAGE VISIBILITY
      ============================================================
    */

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
        return;
      }

      if (
        this.autoplayEnabled &&
        !this.isPaused &&
        !this.isHoverPaused
      ) {
        this.startAutoplay();
      }
    }

    /*
      ============================================================
      THEME EDITOR
      ============================================================
    */

    selectBlock(blockId) {
      const matchingSlide =
        this.slides.find((slide) => {
          return (
            slide.dataset.blockId ===
            blockId
          );
        });

      if (!matchingSlide) return;

      const index =
        this.slides.indexOf(
          matchingSlide
        );

      if (index < 0) return;

      this.stopAutoplay();
      this.showSlide(index, false);
    }

    /*
      ============================================================
      HELPERS
      ============================================================
    */

    escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    destroy() {
      this.stopAutoplay();

      if (this.countdownTimer) {
        window.clearInterval(
          this.countdownTimer
        );

        this.countdownTimer = null;
      }

      document.removeEventListener(
        'visibilitychange',
        this.boundHandleVisibilityChange
      );

      this.section.removeEventListener(
        'mouseenter',
        this.boundHandleMouseEnter
      );

      this.section.removeEventListener(
        'mouseleave',
        this.boundHandleMouseLeave
      );

      delete this.section
        .pianziAnnouncementBar;
    }
  }

  /*
    ============================================================
    INITIALIZATION
    ============================================================
  */

  const initializeSections = (
    root = document
  ) => {
    const sections = [];

    if (
      root.matches?.(
        SECTION_SELECTOR
      )
    ) {
      sections.push(root);
    }

    root
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach((section) => {
      if (
        !section.pianziAnnouncementBar
      ) {
        new PianziAnnouncementBar(
          section
        );
      }
    });
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeSections();
      },
      { once: true }
    );
  } else {
    initializeSections();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeSections(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section =
        event.target.matches?.(
          SECTION_SELECTOR
        )
          ? event.target
          : event.target.querySelector?.(
              SECTION_SELECTOR
            );

      section
        ?.pianziAnnouncementBar
        ?.destroy();
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const section =
        event.target.closest?.(
          SECTION_SELECTOR
        );

      if (!section) return;

      section
        .pianziAnnouncementBar
        ?.selectBlock(
          event.detail?.blockId
        );
    }
  );

  document.addEventListener(
    'shopify:block:deselect',
    (event) => {
      const section =
        event.target.closest?.(
          SECTION_SELECTOR
        );

      const instance =
        section?.pianziAnnouncementBar;

      if (
        instance?.autoplayEnabled &&
        !instance.isPaused
      ) {
        instance.startAutoplay();
      }
    }
  );
})();